import { getProduct } from './index';
import {
  getProductMeasurementSettings,
  getSmallestAndLargest,
} from '../../state/products/productsHelpers.ts';
import { fractionLigatures } from '../../util/measures';
import { DIMENSIONS, MS, TS } from '../../constants';
import { translate } from '../L10n';
import { getMeasurementBySoM } from './productHandler';
import { selectUseMetric } from '../../state/dexfSettings/dexfSettingsSelectors';
import store from '../../state';
import { t } from '../../translations';

export const productMeasurementFormatter = (
  id,
  sectionsSizes,
  measurements,
  data,
  useMetric
) => {
  const product = getProduct(id);

  if (!product) return '';

  const { unit, measurementPrefix, format, measure } =
    getProductMeasurementSettings(product.filter.type);

  /**
   * Checks if the given item fits in all it's parents variants
   * @param dimension {string}
   * @returns {boolean}
   */
  const fitsInAll = dimension =>
    sectionsSizes[dimension].reduce(
      (acc, curr) => (measurements[dimension].includes(curr) ? acc + 1 : acc),
      0
    ) === sectionsSizes[dimension].length;

  /**
   * Returns a string with min and max values
   *
   * @param min {number}
   * @param max {number}
   * @returns {string}
   */
  const formatWithSeveralValues = ({ min, max }) =>
    `${getMeasurementBySoM(useMetric, min, unit)} - ${getMeasurementBySoM(
      useMetric,
      max,
      unit
    )}`;

  /**
   * Formats measurements according to market
   * @param dimension {string}
   * @returns {string}
   */
  const formatMeasurement = dimension => {
    if (!data[dimension].length) return '';

    return data[dimension].length > 1
      ? formatWithSeveralValues(getSmallestAndLargest(data[dimension]))
      : getMeasurementBySoM(useMetric, data[dimension][0], unit);
  };

  /**
   * Returns measurement prefix in form of a capitalized letter for given dimension
   * according to settings
   *
   * @param dimension
   * @returns {string|string}
   */
  const displayMeasurementPrefix = dimension =>
    measurementPrefix
      ? `${translate(t[`SHORT_LABEL_${dimension.toUpperCase()}`])} `
      : '';

  /**
   * Get measurements string
   * @param dimension {string}
   * @returns {string}
   */
  const getMeasurement = dimension =>
    `${displayMeasurementPrefix(dimension)}${formatMeasurement(dimension)}`;

  /**
   * Get start position of next dimension
   * @param contains {array}
   * @param dimension {string}
   * @param index {number}
   * @returns {number}
   */
  const getNextDimensionStartPosition = (contains, index, dimension) =>
    format.indexOf(contains[index + 1].dimension) -
    (format.indexOf(dimension) + dimension.length);

  /**
   * Derives the separation part between two dimensions of the string
   *
   * @param index {number}
   * @param contains {array}
   * @param dimension {string}
   * @returns {string}
   */
  const formatSeparator = (index, contains, dimension) =>
    format.substr(
      format.indexOf(dimension) + dimension.length,
      index === contains.length - 1
        ? format.length
        : getNextDimensionStartPosition(contains, index, dimension)
    );

  /**
   * Sorts array, small to large
   *
   * @param a {object}
   * @param b {object}
   * @returns {number}
   */
  const sortSmallToLarge = (a, b) => a.startsAt - b.startsAt;

  /**
   * Add separator key to object
   *
   * @param obj {object}
   * @param index {number}
   * @param arr {array}
   * @returns {{separator: *}}
   */
  const addSeparatorKey = (obj, index, arr) => ({
    ...obj,
    separator: formatSeparator(index, arr, obj.dimension),
  });

  /**
   * Extracts dimension start and finish index from string
   *
   * @param acc {object}
   * @param dimension {string}
   * @param index {number}
   * @returns {*[]}
   */
  const getDimensionKeywordPositions = (acc, dimension, index) => [
    ...acc,
    {
      dimension,
      startsAt: format.indexOf(dimension),
      endsAt: dimension.length,
    },
  ];

  /**
   *
   * Figures out whether a dimension should be removed from final measurement string
   *
   * @param dimension {string}
   * @returns {*|boolean}
   */
  const removeUnwantedDimensions = ({ dimension }) =>
    format.includes(dimension) && (measure ? !fitsInAll(dimension) : true);

  /**
   * If given separator is the last in the array, remove any trailing comma,
   * else return string
   *
   * @param array
   * @param index
   * @param separator
   * @returns {*}
   */
  const shouldRemoveComma = (array, index, separator) =>
    array.length === index + 1 ? separator.replace(',', '') : separator;

  /**
   * Compiles the final string
   *
   * @param acc
   * @param dimension
   * @param separator
   * @param rest
   * @param index {number}
   * @param array {array}
   * @returns {string}
   */
  const buildMeasurementString = (
    acc,
    { dimension, separator, ...rest },
    index,
    array
  ) =>
    `${acc}${getMeasurement(dimension)}${shouldRemoveComma(
      array,
      index,
      separator
    )}`;

  const useMetricMeasures = selectUseMetric(store.getState());

  return Object.values(DIMENSIONS)
    .reduce(getDimensionKeywordPositions, [])
    .sort(sortSmallToLarge)
    .map(addSeparatorKey)
    .filter(removeUnwantedDimensions)
    .reduce(buildMeasurementString, '')
    .replaceAll(
      MS,
      useMetricMeasures
        ? translate(t.MEASURE_VALUE_CM)
        : translate(t.MEASURE_VALUE_IN)
    )
    .replaceAll(TS, translate(t[TS]))
    .replace(/\d+\/\d+/g, fraction => fractionLigatures[fraction]);
};
